import React, { useMemo } from 'react';
import { useLanguage } from 'Context/LanguageContext';
import { StatusColor } from '@core/constants/task/colorCodes.constants';
import Tags from '~/Tags/Tags.component';
import TaskStatus from '~/Status/TaskStatus';
import Priority from '@core/constants/task/TaskPriority.constants';
import {
	Box,
	Button,
	Typography,
	Tooltip,
	Link as MUILink,
	Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import { CottonCandy, DeepSpaceGrey } from '@core/theme/baseTheme';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TASK_STATUS from '@core/constants/task/TaskStatus.constants';
import { useSelector } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import SmartTaskDetailIcons from './SmartTaskDetailIcons';
import SmartTaskFunctionalities from './SmartTaskFunctionalities';
import { buildTaskDetailIcons, buildFunctionalityIcons } from './SmartTaskIconsUtils';
import StarIcon from '@mui/icons-material/Star';
import parsePhoneNumberFromString from 'libphonenumber-js';
import happyIcon from '@assets/icons/satisfactory-icon-png.png';
import likeIcon from '@assets/icons/good-icon-png.png';
import sadIcon from '@assets/icons/not-satisfactory-icon-png.png';
import dislikeIcon from '@assets/icons/bad-icon-png.png';
import Types from 'FielderUtils/forms/types';

const MAX_TEXT_PREVIEW_LENGTH = 60;
const MAX_LENGTH = 30;
const renderPhoneWithCountryCode = (phone) => {
	if (!phone) return '';
	const parsed = parsePhoneNumberFromString(phone);
	if (!parsed?.isValid()) return phone;
	const countryCode = parsed.country;
	const formatted = parsed.formatInternational();
	return `${countryCode} ${formatted}`;
};
const renderEllipsisContent = ({ value, href }) => {
	const shouldTruncate = value.length > MAX_LENGTH;
	const shortText = shouldTruncate ? `${value.slice(0, MAX_LENGTH)}...` : value;

	const content = (
		<Box
			sx={{
				maxWidth: '100%',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}}
		>
			<Typography
				component="span"
				sx={{
					display: 'inline-block',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					fontSize: '0.75rem',
				}}
			>
				{shortText}
			</Typography>
		</Box>
	);

	const wrappedContent = href ? (
		<MUILink
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			underline="none"
			sx={{
				color: 'inherit',
				display: 'block',
				maxWidth: '100%',
				'&:hover': { color: 'primary.main' },
			}}
		>
			{content}
		</MUILink>
	) : (
		content
	);

	return shouldTruncate ? (
		<Tooltip title={value} arrow>
			{wrappedContent}
		</Tooltip>
	) : (
		wrappedContent
	);
};
const EllipsisAddressLinkCellComponent = ({ value }) => {
	if (!value) return null;
	const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`;
	return renderEllipsisContent({ value, href: mapsUrl });
};
const EllipsisTextCellComponent = ({ value }) => {
	if (!value) return null;
	return renderEllipsisContent({ value });
};
const PhoneCellComponent = ({ value }) => {
	const formatted = renderPhoneWithCountryCode(value);
	return (
		<Tooltip title={`${value}`}>
			<Typography variant="body2" component="span">
				{formatted}
			</Typography>
		</Tooltip>
	);
};
const StarRatingCellComponent = ({ value, row }) => {
	const comment = row?.ratingClientCommentTask;
	return (
		<Tooltip title={comment || ''} arrow>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
				{[1, 2, 3, 4, 5].map((i) => {
					const fillPercent = i <= value ? 100 : i - 1 < value ? (value % 1) * 100 : 0;
					return (
						<Box key={i} sx={{ position: 'relative', width: 20, height: 20, display: 'inline-block', mr: 0.2 }}>
							<StarIcon sx={{ color: (theme) => theme.palette.action.disabled, position: 'absolute', top: 0, left: 0, fontSize: '18px' }} />
							<Box sx={{ width: `${fillPercent}%`, overflow: 'hidden', position: 'absolute', top: 0, left: 0 }}>
								<StarIcon sx={{ color: '#FFD700', fontSize: '18px' }} />
							</Box>
						</Box>
					);
				})}
			</Box>
		</Tooltip>
	);
};
const TaskIconCellComponent = ({ value }) => {
	const lan = useLanguage();
	const taskIcons = React.useMemo(() => buildTaskDetailIcons(value, lan), [value, lan]);
	return <SmartTaskDetailIcons taskIcons={taskIcons} icons={value} />;
};
const TaskTagsCellComponent = ({ value }) => (
	<Grid container alignContent="center">
		<Tags tags={value} />
	</Grid>
);
const DateCellComponent = ({ value, row, idKey }) => {
	const lan = useLanguage();
	const language = lan._language.replace('_', '-');
	const localDate = useMemo(() => {
		if (!value) return '';
		const tz = row?.location?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
		const date = new Date(value);
		try {
			return date.toLocaleString(language, { timeZone: tz });
		} catch (error) {
			console.error(error);
			return date.toLocaleString(language, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
		}
	}, [value, row?.location?.timeZone, language]);
	return <Typography variant="body2" component="span">{localDate}</Typography>;
};
const toDateDisplayString = (question, lan, row) => {
	if (!question) return '';
	const language =
		(lan?._language ? lan._language.replace('_', '-') : null) ||
		(typeof navigator !== 'undefined' ? navigator.language : 'en-US');
	const tz = row?.location?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const fmt = (date) => {
		try {
			return date.toLocaleString(language, { timeZone: tz });
		} catch {
			return date.toLocaleString(language, {
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
		}
	};
	const candidates = [question?.parsedDate, question?.date, question?.value];
	for (const candidate of candidates) {
		if (candidate == null || (typeof candidate === 'string' && !candidate.trim())) continue;
		if (candidate instanceof Date && !Number.isNaN(candidate.getTime())) {
			return fmt(candidate);
		}
		if (typeof candidate === 'number' || (typeof candidate === 'string' && /^\d+$/.test(candidate))) {
			const asNumber = Number(candidate);
			const d = new Date(asNumber);
			if (!Number.isNaN(d.getTime())) return fmt(d);
		}
		const d = new Date(candidate);
		if (!Number.isNaN(d.getTime())) return fmt(d);
	}
	return '';
};
const isOwner = (user, task) => user.idAccount === task.accountId;
const isLastOwner = (user, task) => {
	if (task.partnerTaskRelations.length) {
		const lastElement = task.partnerTaskRelations.length - 1;
		const lastRelation = task.partnerTaskRelations[lastElement];
		return lastRelation.idAccountOwner === user.idAccount;
	}
	return false;
};
const StatusCellComponent = ({ value: status, row: task }) => {
	const user = useSelector(selectUser);
	const text = useMemo(() => {
		if (status === TASK_STATUS.ASSIGNED_TO_PARTNER && !isOwner(user, task) && !isLastOwner(user, task))
			return 'taskStatus_8_1';
		return `taskStatus_${status}`;
	}, [status, task, user]);
	const statusInfo = { text, ...StatusColor[status] };
	return <TaskStatus status={statusInfo} />;
};
const InventoryAssetStatusCellComponent = ({ value: status, maxLength }) => {
	const lan = useLanguage();
	const statusText = status && status.status ? status.status : lan.noStatus;
	const shouldTruncate = statusText.length > maxLength;
	const shortText = shouldTruncate ? `${statusText.slice(0, maxLength)}...` : statusText;
	const Label = (
		<Typography variant="body2" component="span">
			{shortText}
		</Typography>
	);
	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={1}
			sx={{ width: '100%', justifyContent: 'flex-start' }}
			key={statusText}
		>
			{status?.color ? (
				<Box
					sx={{
						width: 10,
						height: 10,
						borderRadius: '50%',
						backgroundColor: status.color,
						flex: '0 0 auto',
						mt: '2px',
					}}
				/>
			) : null}
			{shouldTruncate ? (
				<Tooltip title={statusText} arrow>
					{Label}
				</Tooltip>
			) : (
				Label
			)}
		</Stack>
	);
};

const TaskTypeCellComponent = ({ value }) => {
	const lan = useLanguage();
	return useMemo(() => {
		switch (value) {
			case 'Volume':
				return lan.volume;
			case 'Pickup':
				return lan.pickup;
			case 'Workforce':
				return lan.workforce;
			case 'Delivery':
				return lan.delivery;
			case 'Group Task':
				return lan.grouptask;
			case 'Remote':
				return lan.remote;
			default:
				return value;
		}
	}, [value, lan]);
};
const ParentTaskTypeCellComponent = ({ value }) => {
	const lan = useLanguage();
	return useMemo(() => {
		const mappings = {
			Volume: lan.volume,
			Pickup: lan.pickup,
			Workforce: lan.workforce,
			Delivery: lan.delivery,
			'Group Task': lan.grouptask,
			Remote: lan.remote,
		};
		let result = value;
		Object.keys(mappings).forEach((key) => {
			result = result.replace(new RegExp(`\\b${key}\\b`, 'gi'), mappings[key]);
		});
		return result;
	}, [value, lan]);
};
const PriorityCellComponent = ({ value: priority, row }) => {
	const lan = useLanguage();
	return useMemo(() => {
		switch (priority) {
			case Priority.HIGH:
				return lan.high;
			case Priority.MEDIUM:
				return lan.medium;
			case Priority.LOW:
				return lan.low;
			default:
				return row.priorityName;
		}
	}, [priority, row.priorityName, lan]);
};
const AgentCellComponent = ({ value: agent }) => {
	return useMemo(() => {
		if (agent && agent.name) return `${agent.name} ${agent.lastName}`;
		return '';
	}, [agent]);
};
const ParentTaskCellComponent = ({ value: parentTask }) => {
	return useMemo(() => (parentTask ? parentTask : ''), [parentTask]);
};
const TaskFunctionalitiesCellComponent = ({ value }) => {
	const lan = useLanguage();
	const funcProps = useMemo(() => {
		const { forms = [], assets = [], inventory = [] } = value || {};
		return { forms, assets, inventory };
	}, [value]);
	const icons = React.useMemo(() => buildFunctionalityIcons(funcProps, lan), [funcProps, lan]);
	const hasFunctionalities =
		funcProps.forms.totalCount > 0 || funcProps.assets.totalCount > 0 || funcProps.inventory.totalCount > 0;
	return hasFunctionalities ? <SmartTaskFunctionalities {...funcProps} functionalityIcons={icons} /> : null;
};
const TaskDetailButtonComponent = ({ value: taskId, onClick }) => {
	const theme = useTheme();
	const isDark = theme.palette.mode === 'dark';
	return (
		<Button
			onClick={onClick}
			startIcon={<VisibilityIcon />}
			sx={{
				backgroundColor: isDark ? theme.palette.primary.dark : CottonCandy,
				color: isDark ? theme.palette.common.white : DeepSpaceGrey,
				borderRadius: '12px',
				px: 2,
				py: 0.5,
				textTransform: 'none',
				'&:hover': {
					backgroundColor: isDark ? theme.palette.primary.main : theme.palette.primary.light,
					color: isDark ? theme.palette.common.white : theme.palette.getContrastText(theme.palette.primary.light),
				},
			}}
		>
			{taskId}
		</Button>
	);
};
const toSelectedOptions = (options = []) =>
	options
		.filter((opt) => opt && (opt.checked === true || opt.selected === true))
		.map((opt) => opt.name ?? opt.value ?? opt.comment ?? '')
		.filter((text) => text !== null && text !== undefined && text !== '')
		.join(', ');
const toBooleanValue = (value) => {
	if (typeof value === 'boolean') return value;
	if (typeof value === 'number') return value !== 0;
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		if (normalized === 'true' || normalized === 'yes') return true;
		if (normalized === 'false' || normalized === 'no') return false;
	}
	return null;
};
const toDisplayString = (value) => {
	if (value === null || value === undefined) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'number' && Number.isFinite(value)) return value.toString();
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (Array.isArray(value)) return value.map((v) => toDisplayString(v)).filter(Boolean).join(', ');
	if (value instanceof Date) return value.toISOString();
	return String(value);
};
const createTextNode = (text) => {
	if (!text) return '';
	if (text.length > MAX_TEXT_PREVIEW_LENGTH) {
		const preview = `${text.slice(0, MAX_TEXT_PREVIEW_LENGTH)}…`;
		return (
			<Tooltip title={text} arrow>
				<Typography variant="body2" component="span">{preview}</Typography>
			</Tooltip>
		);
	}
	return <Typography variant="body2" component="span">{text}</Typography>;
};
const toYesNoLabel = (lan, value) => {
	if (value === null) return '';
	const yesLabel = lan?.yes ?? lan?.Yes ?? 'Yes';
	const noLabel = lan?.no ?? lan?.No ?? 'No';
	return value ? yesLabel : noLabel;
};
const getBinaryRatingIcon = (question, boolValue) => {
	const iconType = question?.iconType === 'FINGERS' ? 'FINGERS' : 'FACES';
	if (iconType === 'FINGERS') return boolValue ? likeIcon : dislikeIcon;
	return boolValue ? happyIcon : sadIcon;
};
const toImageFormat = (src, lan, fallbackKey) => {
	if (!src) return { text: '', node: '' };
	const label = lan?.[fallbackKey] ?? lan?.image ?? lan?.images ?? '';
	return {
		text: label ?? '',
		node: (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Box
					component="img"
					src={src}
					alt={label || 'question-image'}
					sx={{ maxHeight: 36, maxWidth: 64, objectFit: 'contain', borderRadius: 0.5 }}
				/>
				{label ? createTextNode(label) : null}
			</Box>
		),
	};
};
const isEmptyValue = (value) =>
	value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
const getFirstNonEmptyValue = (object, paths = []) => {
	for (const path of paths) {
		const segments = Array.isArray(path) ? path : String(path).split('.');
		let current = object;
		for (const segment of segments) {
			if (current == null) break;
			current = current[segment];
		}
		if (!isEmptyValue(current)) return current;
	}
	return undefined;
};
const normalizeBoolean = (value) => {
	if (typeof value === 'string') return value.toLowerCase() === 'true';
	return Boolean(value);
};
const getLanLabel = (lan, key, fallback) => {
	const label = lan?.[key];
	if (!isEmptyValue(label)) return label;
	return fallback;
};
const createEntityDetailsDisplay = (entries) => {
	if (!entries || entries.length === 0) return null;
	const text = entries
		.map(({ label, value }) => {
			if (!label) return value;
			return `${label}: ${value}`;
		})
		.join(' | ');

	return {
		text,
		node: (
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
				{entries.map(({ key, label, value }) => (
					<Typography
						key={key || label || value}
						variant="body2"
						component="div"
						sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}
					>
						{label ? (
							<Box component="span" sx={{ fontWeight: 600 }}>
								{`${label}:`}
							</Box>
						) : null}
						<Box component="span">{value}</Box>
					</Typography>
				))}
			</Box>
		),
	};
};
const formatInventoryQuestionDetails = (question, lan) => {
	const entries = [];
	const idValue = getFirstNonEmptyValue(question, [
		['inventory', 'inventoryId'],
		['inventory', 'id'],
		'inventoryId',
		'value',
	]);
	if (!isEmptyValue(idValue)) {
		entries.push({
			key: 'inventory-id',
			label: getLanLabel(lan, 'id', 'ID'),
			value: toDisplayString(idValue),
		});
	}
	const typeValue = getFirstNonEmptyValue(question, [
		['inventory', 'inventoryType'],
		['inventory', 'type'],
		'inventoryType',
	]);
	if (normalizeBoolean(question?.includeInventoryType) && !isEmptyValue(typeValue)) {
		entries.push({
			key: 'inventory-type',
			label: getLanLabel(lan, 'type', 'Type'),
			value: toDisplayString(typeValue),
		});
	}
	const nameValue = getFirstNonEmptyValue(question, [['inventory', 'name'], 'inventoryName', 'name']);
	if (normalizeBoolean(question?.includeName) && !isEmptyValue(nameValue)) {
		entries.push({
			key: 'inventory-name',
			label: getLanLabel(lan, 'name', 'Name'),
			value: toDisplayString(nameValue),
		});
	}
	const brandValue = getFirstNonEmptyValue(question, [['inventory', 'brand'], 'brand']);
	if (normalizeBoolean(question?.includeBrand) && !isEmptyValue(brandValue)) {
		entries.push({
			key: 'inventory-brand',
			label: getLanLabel(lan, 'brand', 'Brand'),
			value: toDisplayString(brandValue),
		});
	}
	const modelValue = getFirstNonEmptyValue(question, [['inventory', 'model'], 'model']);
	if (normalizeBoolean(question?.includeModel) && !isEmptyValue(modelValue)) {
		entries.push({
			key: 'inventory-model',
			label: getLanLabel(lan, 'model', 'Model'),
			value: toDisplayString(modelValue),
		});
	}
	const serialValue = getFirstNonEmptyValue(question, [['inventory', 'serialNumber'], 'serialNumber']);
	if (normalizeBoolean(question?.includeSerialNumber) && !isEmptyValue(serialValue)) {
		entries.push({
			key: 'inventory-serial-number',
			label: getLanLabel(lan, 'serialNumber', 'Serial Number'),
			value: toDisplayString(serialValue),
		});
	}
	const details = createEntityDetailsDisplay(entries);
	if (details) return details;
	const fallback = toDisplayString(
		getFirstNonEmptyValue(question, [['inventory', 'name'], 'inventoryName', 'inventoryId', 'value']),
	);
	return { text: fallback, node: createTextNode(fallback) };
};
const formatAssetQuestionDetails = (question, lan) => {
	const entries = [];
	const idValue = getFirstNonEmptyValue(question, [
		['asset', 'assetId'],
		['asset', 'id'],
		'assetId',
		'value',
	]);
	if (!isEmptyValue(idValue)) {
		entries.push({
			key: 'asset-id',
			label: getLanLabel(lan, 'id', 'ID'),
			value: toDisplayString(idValue),
		});
	}
	const typeValue = getFirstNonEmptyValue(question, [['asset', 'assetType'], ['asset', 'type'], 'assetType']);
	if (normalizeBoolean(question?.includeAssetType) && !isEmptyValue(typeValue)) {
		entries.push({
			key: 'asset-type',
			label: getLanLabel(lan, 'type', 'Type'),
			value: toDisplayString(typeValue),
		});
	}
	const nameValue = getFirstNonEmptyValue(question, [['asset', 'name'], 'assetName', 'name']);
	if (normalizeBoolean(question?.includeName) && !isEmptyValue(nameValue)) {
		entries.push({
			key: 'asset-name',
			label: getLanLabel(lan, 'name', 'Name'),
			value: toDisplayString(nameValue),
		});
	}
	const brandValue = getFirstNonEmptyValue(question, [['asset', 'brand'], 'brand']);
	if (normalizeBoolean(question?.includeBrand) && !isEmptyValue(brandValue)) {
		entries.push({
			key: 'asset-brand',
			label: getLanLabel(lan, 'brand', 'Brand'),
			value: toDisplayString(brandValue),
		});
	}
	const modelValue = getFirstNonEmptyValue(question, [['asset', 'model'], 'model']);
	if (normalizeBoolean(question?.includeModel) && !isEmptyValue(modelValue)) {
		entries.push({
			key: 'asset-model',
			label: getLanLabel(lan, 'model', 'Model'),
			value: toDisplayString(modelValue),
		});
	}
	const serialValue = getFirstNonEmptyValue(question, [['asset', 'serialNumber'], 'serialNumber']);
	if (normalizeBoolean(question?.includeSerialNumber) && !isEmptyValue(serialValue)) {
		entries.push({
			key: 'asset-serial-number',
			label: getLanLabel(lan, 'serialNumber', 'Serial Number'),
			value: toDisplayString(serialValue),
		});
	}
	const details = createEntityDetailsDisplay(entries);
	if (details) return details;
	const fallback = toDisplayString(getFirstNonEmptyValue(question, [['asset', 'name'], 'assetName', 'assetId', 'value']));
	return { text: fallback, node: createTextNode(fallback) };
};
export const formatFunctionalityQuestionValue = (question, questionType, lan, row) => {
	if (!question) return { text: '', node: '' };
	const resolvedType =
		questionType !== undefined && questionType !== null ? Number(questionType) : Number(question.type);
	switch (resolvedType) {
		case Types.CHECKBOX:
		case Types.RADIOBUTTON:
		case Types.MULTIPLE_CHOICE: {
			const text = toSelectedOptions(question.options);
			return { text, node: createTextNode(text) };
		}
		case Types.TEXT_FIELD: {
			const text = toDisplayString(question.text ?? question.value);
			return { text, node: createTextNode(text) };
		}
		case Types.NUMERIC_RATING:
		case Types.STARS_RATING: {
			const text = toDisplayString(question.value);
			return { text, node: createTextNode(text) };
		}
		case Types.BINARY_RATING: {
			const boolValue = toBooleanValue(question.value);
			if (boolValue === null) {
				const text = toDisplayString(question.value);
				return { text, node: createTextNode(text) };
			}
			const label = toYesNoLabel(lan, boolValue);
			const icon = getBinaryRatingIcon(question, boolValue);
			return {
				text: label,
				node: (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Box component="img" src={icon} alt={label} sx={{ height: 18, width: 18, objectFit: 'contain' }} />
						<Typography variant="body2" component="span">{label}</Typography>
					</Box>
				),
			};
		}
		case Types.CODEBAR_TEXT: {
			const text = toDisplayString(question.value);
			return { text, node: createTextNode(text) };
		}
		case Types.DATE_HOUR: {
			const text = toDateDisplayString(question, lan, row);
			return { text, node: createTextNode(text) };
		}
		case Types.INVENTORY: {
			return formatInventoryQuestionDetails(question, lan);
		}
		case Types.ASSET: {
			return formatAssetQuestionDetails(question, lan);
		}
		case Types.ELECTRONIC_SIGNATURE:
		case Types.FILES:
		case Types.CODEBAR_IMG: {
			const src = question.file ?? question.value;
			return toImageFormat(src, lan, 'image');
		}
		default: {
			const text = toDisplayString(question.value ?? question.text ?? question.answer);
			return { text, node: createTextNode(text) };
		}
	}
};
export const functionalityConfig = {
	FORMS: { section: 'forms', listKey: 'listForms' },
	ASSETS: { section: 'assets', listKey: 'listAssets' },
	INVENTORIES: { section: 'inventory', listKey: 'listInventories' },
};
export const toColumnId = (typeKey, itemId, questionId) => `functionality_${typeKey}_${itemId}_${questionId}`;
export const normalizeFunctionalityKey = (typeKey, searchFunctionalities) => {
	if (!typeKey) return null;
	const upperKey = String(typeKey).toUpperCase();
	if (functionalityConfig[upperKey]) return upperKey;
	if (!searchFunctionalities) return null;
	const matchedEntry = Object.entries(searchFunctionalities).find(([, value]) => value === typeKey);
	if (matchedEntry) {
		const [matchedKey] = matchedEntry;
		return functionalityConfig[matchedKey] ? matchedKey : null;
	}
	return null;
};
const toList = (functionalities, { section, listKey }) => {
	if (!functionalities || !section || !listKey) return [];
	const sectionValue = functionalities[section];
	if (!sectionValue) return [];
	const list = sectionValue[listKey];
	return Array.isArray(list) ? list : [];
};
const findQuestionInItem = (item, questionId) => {
	if (!item) return null;
	const targetId = questionId != null ? String(questionId) : null;
	if (!targetId) return null;
	const searchCollections = [];
	if (Array.isArray(item.fields)) searchCollections.push(item.fields);
	if (Array.isArray(item.questions)) searchCollections.push(item.questions);
	for (const collection of searchCollections) {
		for (const entry of collection) {
			const candidate = entry?.question ?? entry;
			const candidateId =
				candidate?.id ?? candidate?.questionId ?? entry?.id ?? entry?.fieldId ?? entry?.uid;
			if (candidateId != null && String(candidateId) === targetId) {
				return candidate;
			}
		}
	}
	return null;
};
export const getFunctionalityQuestionDisplayValue = (
	functionalities,
	{ functionalityKey, itemId, questionId, questionType, searchFunctionalities, lan, row },
) => {
	const normalizedKey = normalizeFunctionalityKey(functionalityKey, searchFunctionalities);
	if (!normalizedKey) return '';
	const config = functionalityConfig[normalizedKey];
	if (!config) return '';
	const list = toList(functionalities, config);
	const item = list.find((entry) => entry && String(entry.id) === String(itemId));
	if (!item) return '';
	const question = findQuestionInItem(item, questionId);
	return formatFunctionalityQuestionValue(question, questionType, lan, row);
};
export const createFunctionalityQuestionCell = (lan, options) => ({ row }) => {
	const functionalities = row?.original?.functionalities ?? row?.functionalities;
	const formatted = getFunctionalityQuestionDisplayValue(functionalities, { ...options, lan, row });
	if (formatted && typeof formatted === 'object') {
		return formatted.node ?? formatted.text ?? '';
	}
	return formatted ?? '';
};
export const EllipsisAddressLinkCell = React.memo(EllipsisAddressLinkCellComponent);
export const EllipsisTextCell = React.memo(EllipsisTextCellComponent);
export const PhoneCell = React.memo(PhoneCellComponent);
export const StarRatingCell = React.memo(StarRatingCellComponent);
export const TaskIconCell = React.memo(TaskIconCellComponent);
export const TaskTagsCell = React.memo(TaskTagsCellComponent);
export const DateCell = React.memo(DateCellComponent);
export const StatusCell = React.memo(StatusCellComponent);
export const TaskTypeCell = React.memo(TaskTypeCellComponent);
export const ParentTaskTypeCell = React.memo(ParentTaskTypeCellComponent);
export const PriorityCell = React.memo(PriorityCellComponent);
export const AgentCell = React.memo(AgentCellComponent);
export const ParentTaskCell = React.memo(ParentTaskCellComponent);
export const TaskFunctionalitiesCell = React.memo(TaskFunctionalitiesCellComponent);
export const TaskDetailButton = React.memo(TaskDetailButtonComponent);
export const InventoryAssetStatusCell = React.memo(InventoryAssetStatusCellComponent);
